import { onDestroy, onMount } from 'svelte';
import { get, writable, type Writable } from 'svelte/store';
import { add, remove } from '@store/collections.svelte.ts';
import { v4 as uuidv4 } from 'uuid';
import type { RFCanvasMeta, RFCanvas, ReframeCollection, ReframeCollectionMeta, ReframeCollectionModule } from '@src/types/reframe';
import { useSlottedElements } from '@src/composables/useSlottedElements';
import { addNotification } from '@store/notification-centre.svelte';
import type { ReframeTextComponent } from '@core/elements/rf-text/rf-text.svelte';

export function useCollection(
	collectionId: string | undefined = undefined,
	{
		element: bindedElement = writable(null),
		moduleName = '',
		componentId = '',
	}: { element?: Writable<HTMLElement | null>; moduleName?: string; attributes?: object; componentId?: string } = {}
) {
	/**
	 * States
	 */
	const uuid: { value: string } = $state({ value: '' });
	const id: string | undefined = $state(collectionId);
	const canvases: RFCanvasMeta[] = $state([]);
	const meta: ReframeCollectionMeta = $state({
		titles: [],
		descriptions: [],
	});
	const module: ReframeCollectionModule = $state({
		name: moduleName,
	});

	/**
	 * Composables
	 */

	const { element, getSlottedElements } = useSlottedElements({ element: bindedElement });

	/**
	 * Methods
	 */

	/**
	 * Adds a new item to the given list only if an item with the same `tag` and `content` doesn't already exist.
	 *
	 * @param list - The array to add the item to. Each item is expected to have `tag` and `content` properties.
	 * @param tag - The tag value of the new item.
	 * @param content - The content value of the new item.
	 */
	const addUniqueMetaItem = (list: Array<{ tag: string; content: string }>, tag: string, content: string) => {
		const item = { tag, content };
		if (!list.some((entry) => entry.tag === tag && entry.content === content)) {
			list.push(item);
		}
	};

	/**
	 * Processes a text node (e.g., <rf-text>) and adds its metadata to the appropriate list in `meta`,
	 * based on its type ('title' or 'description'). Ensures duplicates are not added.
	 *
	 * @param node - The text component node to process. Expected to have a `tag`, `innerText`, and `innerHTML`.
	 * @param meta - An object containing `titles` and `descriptions` arrays where metadata will be stored.
	 */
	const processTextNode = (node: ReframeTextComponent, meta: any) => {
		const type = node.getAttribute('type') || node.getAttribute('data-reframe-type');
		const tag = node.tag || node.tagName.toLowerCase();

		if (type === 'title') {
			const content = node.innerText.replace(/\s+/g, ' ').trim();
			addUniqueMetaItem(meta.titles, tag, content);
		} else if (type === 'description') {
			const content = node.innerHTML.replace(/\s+/g, ' ').trim();
			addUniqueMetaItem(meta.descriptions, tag, content);
		}
	};

	/**
	 * Processes an <rf-canvas> element by extracting its metadata and legend,
	 * then appends the combined result to the `canvases` array.
	 *
	 * @param node - The canvas element to process. Expected to be an RFCanvas with `meta` and `getLegendMeta()`.
	 * @param canvases - The array where the processed canvas metadata will be stored.
	 */
	const processCanvasNode = (node: Element, canvases: any[]) => {
		const rfCanvas = node as RFCanvas;
		const legendMeta = rfCanvas.getLegendMeta();
		rfCanvas.setCollectionId(uuid.value);
		canvases.push({ ...rfCanvas.meta, legend: legendMeta, thumbnail: rfCanvas.getThumbnail(), getCanvasSize: rfCanvas.getCanvasSize });
	};

	/**
	 * Performs a breadth-first traversal over a set of DOM elements,
	 * applying specific logic when encountering certain custom elements.
	 *
	 * - Extracts meta title and description from <rf-text> elements with type "title" or "description".
	 * - Extracts canvas metadata and legend from <rf-canvas> or elements with class "rf-canvas".
	 *
	 * @param items - A NodeList of root elements to begin traversal from.
	 * @param maxDepth - The maximum depth to traverse. Defaults to 10.
	 */
	const iterateBFS = (items: NodeListOf<Element> | Element[], maxDepth = 10) => {
		const queue: { node: Element; depth: number }[] = Array.from(items).map((node) => ({ node, depth: 0 }));

		while (queue.length > 0) {
			const { node, depth } = queue.shift()!;

			// If we haven't reached the max depth, add children to the queue
			if (depth < maxDepth) {
				const children = node.querySelectorAll('*');
				children.forEach((child) => queue.push({ node: child, depth: depth + 1 }));
			}
			if (!(node instanceof HTMLElement)) return;

			if (node.tagName === 'RF-TEXT' || node.hasAttribute('data-reframe-type')) {
				processTextNode(node as ReframeTextComponent, meta);
			} else if (node.tagName === 'RF-CANVAS' || node.classList.contains('rf-canvas')) {
				processCanvasNode(node, canvases);
			}
		}
	};

	/**
	 * Goes through every slotted items to check if its either a :
	 * - rf-text
	 * - rf-canvas
	 * - data-reframe-type="title" or data-reframe-type="description"
	 */
	const parseModuleSlots = () => {
		const slots = getSlottedElements('*');

		slots.forEach((slot) => {
			iterateBFS([slot]);
		});
	};

	/**
	 *
	 * @param el parent element
	 * @returns {ReframeCollection} Collection regrouping every available data in web component
	 */
	const createCollection = (el: HTMLElement | null = null): ReframeCollection | void => {
		if (!el)
			return addNotification({
				componentId,
				title: 'No parent element',
				message: 'Please add a parent element to the web component',
				severity: 'warning',
				dismissable: true,
			});

		uuid.value = uuidv4();
		parseModuleSlots();

		return {
			id,
			uuid: uuid.value,
			canvases: [...canvases],
			module: { ...module },
			meta: { titles: [...meta.titles], descriptions: [...meta.descriptions] },
		};
	};

	/**
	 * Hooks
	 */

	onMount(() => {
		// get HTML element from writable()
		const el = get(bindedElement) ?? get(element);

		if (!el) return;

		const collection = createCollection(el);

		if (collection) add(collection);
	});

	onDestroy(() => {
		remove(uuid.value);
	});

	return {
		element,
		get id() {
			return id;
		},
		get uuid() {
			return uuid.value;
		},
		get canvases() {
			return [...canvases];
		},
		get module() {
			return { ...module };
		},
		get meta() {
			return { ...meta };
		},
	};
}
